/**
 * Public certificate verification — no auth. Accessed via QR scan or direct URL.
 * Routes: /verify  (search by serial)  and  /verify/:serial  (direct)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, CheckCircle2, XCircle, Search, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyCertificatePage() {
  const { serial: serialParam } = useParams();
  const navigate = useNavigate();
  const [serial, setSerial] = useState(serialParam || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async (s) => {
    if (!s) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/public/certificates/verify/${encodeURIComponent(s)}`);
      const data = await r.json();
      setResult(data);
    } catch {
      setResult({ valid: false, reason: 'Network error — please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (serialParam) verify(serialParam); /* eslint-disable-next-line */ }, [serialParam]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        <div className="text-center mb-6">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-indigo-500/20 border border-indigo-400/40 items-center justify-center mb-3">
            <Shield className="h-7 w-7 text-indigo-300" />
          </div>
          <h1 className="text-2xl font-semibold text-white">Certificate Verification</h1>
          <p className="text-sm text-slate-400 mt-1">Confirm the authenticity of a certificate issued by our institution.</p>
        </div>

        <Card className="bg-white/95 backdrop-blur border-white/10">
          <CardHeader>
            <CardTitle className="text-base">Enter certificate serial number</CardTitle>
            <CardDescription>You can also scan the QR code on the certificate.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <form onSubmit={e => { e.preventDefault(); navigate(`/verify/${encodeURIComponent(serial)}`); verify(serial); }} className="flex gap-2">
              <Input
                value={serial}
                onChange={e => setSerial(e.target.value.trim())}
                placeholder="CERT-2026-000001-ABCDEF"
                className="font-mono text-sm"
                data-testid="verify-serial-input"
              />
              <Button type="submit" disabled={!serial || loading} data-testid="verify-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </form>

            {result && (
              <div
                className={`p-4 rounded-lg border mt-4 ${result.valid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                data-testid={result.valid ? 'verify-result-valid' : 'verify-result-invalid'}
              >
                <div className="flex items-start gap-3">
                  {result.valid
                    ? <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    : <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 text-sm">
                    {result.valid ? (
                      <>
                        <p className="font-semibold text-emerald-900">Certificate Verified ✓</p>
                        <dl className="mt-2 space-y-1 text-slate-700">
                          <div className="flex justify-between gap-4"><dt className="text-slate-500">Recipient</dt><dd className="font-medium text-right">{result.recipient_name}</dd></div>
                          {result.certificate_title && <div className="flex justify-between gap-4"><dt className="text-slate-500">Certificate</dt><dd className="text-right">{result.certificate_title}</dd></div>}
                          {result.certificate_type && <div className="flex justify-between gap-4"><dt className="text-slate-500">Type</dt><dd className="text-right capitalize">{result.certificate_type}</dd></div>}
                          {result.program && <div className="flex justify-between gap-4"><dt className="text-slate-500">Program</dt><dd className="text-right">{result.program}</dd></div>}
                          {result.graduation_year && <div className="flex justify-between gap-4"><dt className="text-slate-500">Graduation Year</dt><dd className="text-right">{result.graduation_year}</dd></div>}
                          <div className="flex justify-between gap-4"><dt className="text-slate-500">Issue Date</dt><dd className="text-right">{result.issue_date}</dd></div>
                          <div className="flex justify-between gap-4"><dt className="text-slate-500">Institution</dt><dd className="text-right">{result.institution}</dd></div>
                          <div className="flex justify-between gap-4"><dt className="text-slate-500">Serial</dt><dd className="text-right"><code className="font-mono text-xs">{result.serial}</code></dd></div>
                        </dl>
                        <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-center">
                          <img
                            src={`${API_URL}/api/public/certificates/qr/${result.serial}.png?base_url=${encodeURIComponent(window.location.origin)}&size=6`}
                            alt="Verification QR"
                            className="h-28 w-28"
                            data-testid="verify-qr-image"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-red-900">Not Valid</p>
                        <p className="text-red-800 mt-1">{result.reason}</p>
                        {result.serial && <p className="text-xs text-red-700 mt-1">Serial: <code className="font-mono">{result.serial}</code></p>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-400 text-center pt-2">
              Privacy-first: we only show enough to confirm authenticity. Full records stay private.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
